/**
 * AR screenshot: the passthrough camera image with the wheels over it.
 *
 * Why this is not `canvas.toDataURL()`. In immersive-ar the camera feed
 * is composited by the SYSTEM, not drawn into our canvas — our canvas
 * holds only the wheels on transparency. A canvas capture would save a
 * wheel floating on nothing, which is worse than no button at all.
 *
 * WebXR Raw Camera Access is the one way a page can obtain that image:
 * with 'camera-access' granted, `XRWebGLBinding.getCameraImage()` hands
 * back the frame as a GL texture. From there this module reads it into
 * pixels, flips it (GL rows run bottom-up, canvases top-down), and
 * paints the scene over it.
 *
 * Everything degrades to null rather than throwing: the feature is
 * Chrome-Android only and may be refused per-session, so the caller
 * tells the user capture is unavailable instead of a handler dying
 * inside a click.
 */

import * as THREE from 'three'
import { atoLog } from '@/services/ato-log'

/** WebXR Raw Camera Access is newer than the bundled WebXR types, so
 *  the two shapes it adds are declared locally rather than pulling a
 *  whole types package for them. */
interface XRCameraLike {
  readonly width: number
  readonly height: number
}

export interface CaptureDeps {
  session: XRSession | null
  frame: XRFrame | null
  referenceSpace: XRReferenceSpace | null
  renderer: THREE.WebGLRenderer | null
  scene: THREE.Scene | null
  camera: THREE.PerspectiveCamera | null
}

/** Why a capture could not be produced — surfaced to the user as-is. */
export type CaptureFailure =
  | 'no-session'
  | 'no-frame'
  | 'no-camera-access'
  | 'read-failed'

export type CaptureResult =
  | { ok: true; blob: Blob }
  | { ok: false; reason: CaptureFailure }

/** Filename for a saved capture. Sortable, and distinct per second so a
 *  burst of taps does not overwrite one file. */
export function captureFileName(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    'autoviz-ar-',
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
    '.png',
  ].join('')
}

/** Human-readable outcome for the toast. */
export function captureMessage(result: CaptureResult): string {
  if (result.ok) return 'Photo saved to your device'
  switch (result.reason) {
    case 'no-camera-access':
      return 'Photo capture is not supported on this device'
    case 'no-session':
    case 'no-frame':
      return 'Start AR before taking a photo'
    case 'read-failed':
    default:
      return "Couldn't save the photo — please try again"
  }
}

/**
 * GL rows run bottom-up and canvas rows top-down, so a raw readPixels
 * buffer arrives upside down. Pure, so the flip is verifiable without a
 * GL context — getting it wrong yields a silently inverted photo.
 */
export function flipPixelRows(
  pixels: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
): Uint8ClampedArray {
  const rowBytes = width * 4
  const out = new Uint8ClampedArray(pixels.length)
  for (let row = 0; row < height; row += 1) {
    const from = row * rowBytes
    const to = (height - row - 1) * rowBytes
    out.set(pixels.subarray(from, from + rowBytes), to)
  }
  return out
}

/**
 * Aspect ratio for the off-screen capture pass.
 *
 * The camera image has its own dimensions, which need not match the
 * screen the scene was last projected for. Falls back to the live
 * aspect on degenerate input rather than producing a NaN projection —
 * a broken matrix renders nothing at all.
 */
export function captureAspect(
  width: number,
  height: number,
  fallback: number,
): number {
  if (!(width > 0) || !(height > 0)) return fallback
  const aspect = width / height
  return Number.isFinite(aspect) && aspect > 0 ? aspect : fallback
}

/**
 * Puts a capture render target on the SAME output pipeline as the live
 * canvas.
 *
 * three.js applies tone mapping and the output colour-space encode only
 * when drawing to the default framebuffer, or to a target flagged as an
 * XR one. For any other render target it forces `NoToneMapping` and
 * `LinearSRGBColorSpace` (three 0.173.0, `WebGLRenderer` +
 * `WebGLPrograms.getParameters`). The off-screen capture pass is
 * "any other render target", so it silently dropped both the
 * ACESFilmicToneMapping curve and the exposure that `scene-lighting.ts`
 * sets on the renderer, and wrote linear-light bytes into an sRGB
 * canvas — a photo noticeably flatter and darker than the live view,
 * with the scene lighting itself never at fault (2026-08-24).
 *
 * Setting the two properties three sets on its own XR framebuffer
 * restores the live pipeline exactly. The `isXRRenderTarget` flag has a
 * second effect that is also the one we want: it forces a LINEAR
 * transfer when picking the texture's internal format, so the buffer
 * stays plain RGBA8 and the GPU does not encode a second time on top of
 * the shader's encode.
 */
export function configureCaptureTarget(target: THREE.WebGLRenderTarget): void {
  target.texture.colorSpace = THREE.SRGBColorSpace
  ;(target as unknown as { isXRRenderTarget: boolean }).isXRRenderTarget = true
}

/** Is raw camera access actually usable this session? */
export function hasCameraAccess(session: XRSession | null): boolean {
  if (session == null) return false
  const enabled = (session as unknown as { enabledFeatures?: string[] })
    .enabledFeatures
  // Older runtimes omit enabledFeatures; the binding call below is the
  // real test, so treat "unknown" as worth attempting.
  if (enabled == null) return true
  return enabled.includes('camera-access')
}

/**
 * Captures the current AR frame. Returns a PNG blob, or a reason it
 * could not.
 */
export async function captureArFrame(deps: CaptureDeps): Promise<CaptureResult> {
  const { session, frame, referenceSpace, renderer, scene, camera } = deps
  if (session == null || renderer == null || scene == null || camera == null) {
    return { ok: false, reason: 'no-session' }
  }
  if (frame == null || referenceSpace == null) {
    return { ok: false, reason: 'no-frame' }
  }
  if (!hasCameraAccess(session)) {
    return { ok: false, reason: 'no-camera-access' }
  }

  let scenePixelsOut = new Uint8Array(0)
  try {
    const pose = frame.getViewerPose(referenceSpace)
    const view = pose?.views?.[0]
    // XRCamera rides on the view only when camera-access was granted.
    const xrCamera = (view as unknown as { camera?: XRCameraLike } | undefined)?.camera
    if (view == null || xrCamera == null) {
      return { ok: false, reason: 'no-camera-access' }
    }

    const gl = renderer.getContext() as WebGL2RenderingContext
    const binding = new (window as unknown as {
      XRWebGLBinding: new (s: XRSession, g: WebGL2RenderingContext) => {
        getCameraImage?: (c: XRCameraLike) => WebGLTexture | null
      }
    }).XRWebGLBinding(session, gl)
    const cameraTexture = binding.getCameraImage?.(xrCamera) ?? null
    if (cameraTexture == null) {
      return { ok: false, reason: 'no-camera-access' }
    }

    const { width, height } = xrCamera
    if (!(width > 0) || !(height > 0)) {
      return { ok: false, reason: 'read-failed' }
    }

    // Read the camera texture by attaching it to a scratch framebuffer.
    const framebuffer = gl.createFramebuffer()
    const cameraPixels = new Uint8Array(width * height * 4)
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      cameraTexture,
      0,
    )
    const complete =
      gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE
    if (complete) {
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, cameraPixels)
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.deleteFramebuffer(framebuffer)
    if (!complete) return { ok: false, reason: 'read-failed' }

    const output = document.createElement('canvas')
    output.width = width
    output.height = height
    const ctx = output.getContext('2d')
    if (ctx == null) return { ok: false, reason: 'read-failed' }
    ctx.putImageData(
      new ImageData(
        new Uint8ClampedArray(flipPixelRows(cameraPixels, width, height)),
        width,
        height,
      ),
      0,
      0,
    )

    // Re-render the scene off-screen at the camera image's size. The XR
    // framebuffer itself cannot be read back mid-session.
    //
    // Two things have to be borrowed and given back, and getting either
    // wrong loses wheels from the photo while the live view stays
    // perfect (2026-08-24 LUAT):
    //
    //  - xr.enabled. While it is on, three renders through the session's
    //    own array camera and IGNORES the camera passed to render(). The
    //    off-screen pass would then use the XR viewport rather than ours.
    //  - the camera's aspect. It is set for the SCREEN, but the camera
    //    image has its own dimensions; leaving it be projects the scene
    //    for the wrong frame, and geometry near an edge lands outside
    //    the picture entirely.
    const target = new THREE.WebGLRenderTarget(width, height)
    // Without this the pass renders untone-mapped and unencoded — see
    // configureCaptureTarget.
    configureCaptureTarget(target)
    const previousTarget = renderer.getRenderTarget()
    const previousXrEnabled = renderer.xr.enabled
    const previousAspect = camera.aspect
    try {
      renderer.xr.enabled = false
      camera.aspect = captureAspect(width, height, previousAspect)
      camera.updateProjectionMatrix()
      renderer.setRenderTarget(target)
      renderer.render(scene, camera)
      renderer.readRenderTargetPixels(
        target,
        0,
        0,
        width,
        height,
        (scenePixelsOut = new Uint8Array(width * height * 4)),
      )
    } finally {
      // Restored in a finally: leaving xr.enabled false would freeze the
      // live AR view for the rest of the session — a far worse failure
      // than a missing photo.
      renderer.setRenderTarget(previousTarget)
      renderer.xr.enabled = previousXrEnabled
      camera.aspect = previousAspect
      camera.updateProjectionMatrix()
      target.dispose()
    }
    const scenePixels = scenePixelsOut

    const overlay = document.createElement('canvas')
    overlay.width = width
    overlay.height = height
    const overlayCtx = overlay.getContext('2d')
    if (overlayCtx == null) return { ok: false, reason: 'read-failed' }
    overlayCtx.putImageData(
      new ImageData(
        new Uint8ClampedArray(flipPixelRows(scenePixels, width, height)),
        width,
        height,
      ),
      0,
      0,
    )
    ctx.drawImage(overlay, 0, 0)

    const blob = await new Promise<Blob | null>((resolve) => {
      output.toBlob(resolve, 'image/png')
    })
    if (blob == null) return { ok: false, reason: 'read-failed' }
    return { ok: true, blob }
  } catch (err) {
    // Any GL/runtime refusal lands here — the button must not throw
    // inside a click handler. The reason code alone shipped for a year
    // while the actual GL error was discarded; report both.
    atoLog.error('ar_capture', err, { reason: 'read-failed' })
    return { ok: false, reason: 'read-failed' }
  }
}

/** Hands the blob to the browser as a download. */
export function saveCapture(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Give the download a tick to start before the URL is reclaimed.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
